import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { geoBounds, geoMercator } from "d3-geo";
import { Star, ChevronRight, MapPin } from "lucide-react";
import { coordsForCity } from "./cityCoords";
import topo from "./india-topo.json";

/**
 * Renders a focused state map (e.g. Bihar) with city dots showing
 * each city's top trades. Replaces the previous bar-only TopStateDetail widget.
 */

// State-name normalisation (TopoJSON spellings ↔ DB spellings)
const ALIASES = {
  "arunanchal pradesh": "arunachal pradesh",
  "nct of delhi": "delhi",
  "national capital territory of delhi": "delhi",
  "andaman & nicobar island": "andaman & nicobar islands",
  "jammu & kashmir": "jammu and kashmir",
  "dadra and nagar haveli": "dadra & nagar haveli",
  "daman and diu": "daman & diu",
  "orissa": "odisha",
  "uttaranchal": "uttarakhand",
};
const canon = (s) => {
  const n = (s || "").trim().toLowerCase().replace(/\s+/g, " ");
  return ALIASES[n] || n;
};

export default function TopStateMap({ detail, segment, rank = 1 }) {
  const navigate = useNavigate();
  const [hover, setHover] = useState(null);

  const stateName = detail?.state || "";
  const cities = detail?.cities || [];
  const rankLabel = rank === 2 ? "State With 2nd Highest Manpower" : "State With Highest Manpower";

  // Find the geography matching the top state to compute its center & scale
  const stateFeature = useMemo(() => {
    const objs = topo?.objects || {};
    const firstKey = Object.keys(objs)[0];
    const feats = (topo?.objects?.[firstKey]?.geometries) || [];
    // Build minimal feature-collection-style search using react-simple-maps geographies on the client.
    // Match by canon(name) — we'll pick it during rendering.
    return canon(stateName);
  }, [stateName]);

  // Cities that have coords
  const plotted = useMemo(() => {
    const out = [];
    cities.forEach((c) => {
      const co = coordsForCity(c.city);
      if (co) out.push({ ...c, coords: co });
    });
    return out;
  }, [cities]);

  // Compute projection center & scale so the box frames the UNION of:
  //   - the target state's polygon bounds (from topo)
  //   - every city marker we're going to plot
  // (Some bundled topos drop small administrative arms — without this the city
  //  dot can land outside the visible frame even though it's inside the state.)
  const projectionConfig = useMemo(() => {
    try {
      const objs = topo?.objects || {};
      const firstKey = Object.keys(objs)[0];
      const geomList = (topo?.objects?.[firstKey]?.geometries) || [];
      const match = geomList.find((g) => canon(g.properties?.name) === stateFeature);
      if (!match) return { scale: 1200, center: [82, 22], aspect: 4 / 3 };
      const arcs = topo.arcs;
      const transform = topo.transform;
      const feature = topoToGeoFeature(match, arcs, transform);
      let [[minX, minY], [maxX, maxY]] = geoBounds(feature);
      // Union with city markers
      plotted.forEach(({ coords: [lon, lat] }) => {
        if (lon < minX) minX = lon;
        if (lon > maxX) maxX = lon;
        if (lat < minY) minY = lat;
        if (lat > maxY) maxY = lat;
      });
      // Small breathing margin around the union
      const padX = Math.max(0.15, (maxX - minX) * 0.05);
      const padY = Math.max(0.15, (maxY - minY) * 0.05);
      minX -= padX; maxX += padX; minY -= padY; maxY += padY;
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      const dx = Math.max(0.5, maxX - minX);
      const dy = Math.max(0.5, maxY - minY);
      // Box aspect now MATCHES the state's lon/lat aspect so the shape fills the frame
      // (Mercator stretches lat slightly — approximate adjustment using cy)
      const latStretch = 1 / Math.cos((cy * Math.PI) / 180);
      // Render at a logical square viewBox so both side-by-side state panels are visually equal-sized
      const vbW = 800;
      const vbH = 800;
      // Apply a Mercator-aware horizontal scale factor so dx and dy compete on equal footing
      const usableWidthPx = vbW * 0.94;
      const usableHeightPx = vbH * 0.94;
      const scaleByWidth = (usableWidthPx / dx) * (180 / Math.PI);
      const scaleByHeight = (usableHeightPx / (dy * latStretch)) * (180 / Math.PI);
      const scale = Math.max(2400, Math.min(22000, Math.min(scaleByWidth, scaleByHeight)));
      return { scale, center: [cx, cy], aspect: 1, vbW, vbH };
    } catch (e) {
      return { scale: 1200, center: [82, 22], aspect: 1, vbW: 800, vbH: 800 };
    }
  }, [stateFeature, plotted]);
  const maxWorkers = Math.max(1, ...plotted.map((c) => c.workers));

  // Lightweight label-collision avoidance.
  // For each city we compute its projected (viewBox) pixel position using the
  // SAME projection ComposableMap will apply, then estimate a label-box size
  // and run a tiny iterative push-apart so overlapping callouts don't pile up.
  // Cities that end up offset get a thin cyan leader line back to their dot.
  const labelLayout = useMemo(() => {
    const VB_W = projectionConfig.vbW || 800;
    const VB_H = projectionConfig.vbH || 600; // matches <ComposableMap width/height/>
    const proj = geoMercator()
      .scale(projectionConfig.scale)
      .center(projectionConfig.center)
      .translate([VB_W / 2, VB_H / 2]);

    // Initial state: place each label directly under its dot (dy = +24).
    const items = plotted.map((c, i) => {
      const [px, py] = proj(c.coords) || [VB_W / 2, VB_H / 2];
      const radius = 5 + Math.round((c.workers / maxWorkers) * 9);
      // Approximate label box (city / N workers / trade — 3 lines stacked)
      const longest = Math.max(
        c.city.length,
        `${c.workers} workers`.length,
        (c.top_trades?.[0]?.trade || "").length
      );
      const w = Math.max(56, longest * 6.6);  // ≈6.6 px per char @ font-size 12
      const h = 40;                            // 3 lines × ~13 px
      return { i, city: c.city, px, py, radius, w, h, dx: 0, dy: radius + 16 };
    });

    // Iterative repulsion (cheap: O(n²) but we max at ~12 cities)
    const ITER = 80;
    const PUSH = 1.4;
    const VB_EDGE_PAD = 8;
    for (let it = 0; it < ITER; it++) {
      let movedAny = false;
      for (let a = 0; a < items.length; a++) {
        const A = items[a];
        const Ax = A.px + A.dx - A.w / 2;
        const Ay = A.py + A.dy - 4;
        for (let b = a + 1; b < items.length; b++) {
          const B = items[b];
          const Bx = B.px + B.dx - B.w / 2;
          const By = B.py + B.dy - 4;
          if (Ax < Bx + B.w && Ax + A.w > Bx && Ay < By + B.h && Ay + A.h > By) {
            const cdx = (Ax + A.w / 2) - (Bx + B.w / 2);
            const cdy = (Ay + A.h / 2) - (By + B.h / 2);
            const dist = Math.hypot(cdx, cdy) || 1;
            const nx = (cdx / dist) * PUSH;
            const ny = (cdy / dist) * PUSH;
            A.dx += nx; A.dy += ny;
            B.dx -= nx; B.dy -= ny;
            movedAny = true;
          }
        }
        // Keep labels inside the viewBox
        const left = A.px + A.dx - A.w / 2;
        const right = A.px + A.dx + A.w / 2;
        const top = A.py + A.dy - 4;
        const bottom = A.py + A.dy + A.h;
        if (left < VB_EDGE_PAD) A.dx += (VB_EDGE_PAD - left);
        if (right > VB_W - VB_EDGE_PAD) A.dx -= (right - (VB_W - VB_EDGE_PAD));
        if (top < VB_EDGE_PAD) A.dy += (VB_EDGE_PAD - top);
        if (bottom > VB_H - VB_EDGE_PAD) A.dy -= (bottom - (VB_H - VB_EDGE_PAD));
      }
      if (!movedAny) break;
    }

    const map = {};
    items.forEach((it) => {
      const homeDy = it.radius + 16;
      const drift = Math.hypot(it.dx, it.dy - homeDy);
      map[it.city] = { dx: it.dx, dy: it.dy, needsLeader: drift > 6 };
    });
    return map;
  }, [plotted, projectionConfig, maxWorkers]);

  if (!detail) return null;

  return (
    <div
      data-testid="panel-top-state-map"
      className="rounded-md border-2 border-slate-900 bg-white p-3 shadow-[3px_3px_0_0_rgba(2,21,46,0.85)]"
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between border-b border-slate-200 pb-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-sm bg-ncc-gold text-slate-900">
            <Star className="h-4 w-4" />
          </span>
          <div>
            <div className="industrial-label text-[10px] text-slate-500">{rankLabel}</div>
            <div className="font-heading text-base font-black text-slate-900">{stateName}</div>
          </div>
        </div>
        <button
          onClick={() => navigate(`/directory?state=${encodeURIComponent(stateName)}`)}
          data-testid="top-state-map-view-all"
          className="inline-flex items-center gap-1 border-2 border-slate-900 bg-ncc-gold px-2 py-1 industrial-label text-[10px] font-bold text-slate-900 hover:bg-yellow-300"
        >
          View Directory <ChevronRight className="h-3 w-3" />
        </button>
      </div>

      {/* Stat strip */}
      <div className="mb-3 grid grid-cols-3 gap-2">
        <div className="border-2 border-slate-900 bg-slate-50 p-2 text-center">
          <div className="industrial-label text-[10px] text-slate-500">Total Workers</div>
          <div className="mt-1 font-heading text-2xl font-black text-slate-900">{detail.total_workers || 0}</div>
          {(detail.total_workers_contractor != null || detail.total_workers_payroll != null) && segment === "all" && (
            <div className="mt-0.5 font-mono text-[10px] font-bold text-slate-700" data-testid={`top-state-${detail.state}-split`}>
              CONT {detail.total_workers_contractor || 0} · PAY {detail.total_workers_payroll || 0}
            </div>
          )}
        </div>
        <div className="border-2 border-slate-900 bg-slate-50 p-2 text-center">
          <div className="industrial-label text-[10px] text-slate-500">Cities</div>
          <div className="mt-1 font-heading text-2xl font-black text-slate-900">{cities.length}</div>
        </div>
        <div className="border-2 border-slate-900 bg-slate-50 p-2 text-center">
          <div className="industrial-label text-[10px] text-slate-500">Mapped</div>
          <div className="mt-1 font-heading text-2xl font-black text-slate-900">{plotted.length}</div>
        </div>
      </div>

      {/* Map (full width) — list of cities moves to a row BELOW the map */}
      <div className="space-y-3">
        {/* Map — square, fills full card width; state shape uses Mercator-aware scale */}
        <div
          className="relative w-full overflow-hidden rounded border-2 border-slate-900 bg-[#02152E]"
          style={{ aspectRatio: String(projectionConfig.aspect || 1) }}
        >
          <ComposableMap
            projection="geoMercator"
            projectionConfig={projectionConfig}
            width={projectionConfig.vbW || 800}
            height={projectionConfig.vbH || 600}
            style={{ width: "100%", height: "100%", display: "block" }}
          >
            <Geographies geography={topo}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const name = geo.properties?.name || "";
                  const isTarget = canon(name) === stateFeature;
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={isTarget ? "#F7B500" : "#0b1f3d"}
                      stroke={isTarget ? "#fde68a" : "#143570"}
                      strokeWidth={isTarget ? 1.2 : 0.4}
                      style={{
                        default: { outline: "none" },
                        hover: { outline: "none" },
                        pressed: { outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>

            {plotted.map((c, idx) => {
              const radius = 5 + Math.round((c.workers / maxWorkers) * 9);
              const lay = labelLayout[c.city] || { dx: 0, dy: radius + 16, needsLeader: false };
              return (
                <Marker
                  key={`${c.city}-${idx}`}
                  coordinates={c.coords}
                  onMouseEnter={(e) => setHover({ ...c, x: e.clientX, y: e.clientY })}
                  onMouseMove={(e) => setHover({ ...c, x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => {
                    const params = new URLSearchParams();
                    params.set("state", stateName);
                    params.set("city", c.city);
                    if (segment) params.set("segment", segment);
                    navigate(`/directory?${params.toString()}`);
                  }}
                  data-testid={`state-map-marker-${c.city}`}
                  style={{ cursor: "pointer" }}
                >
                  {/* Animated halo */}
                  <circle r={radius + 5} fill="#06B6D4" opacity={0.25}>
                    <animate attributeName="r" from={radius + 4} to={radius + 12} dur="1.6s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.35" to="0" dur="1.6s" repeatCount="indefinite" />
                  </circle>
                  {/* Dot */}
                  <circle r={radius} fill="#06B6D4" stroke="#ffffff" strokeWidth={1.2} />

                  {/* Leader line to offset label */}
                  {lay.needsLeader && (
                    <line
                      x1={0}
                      y1={0}
                      x2={lay.dx}
                      y2={lay.dy - 4}
                      stroke="#06B6D4"
                      strokeWidth={0.9}
                      strokeOpacity={0.65}
                      strokeDasharray="2 2"
                    />
                  )}

                  {/* Stacked label group (city / total workers / top trade) */}
                  <g transform={`translate(${lay.dx}, ${lay.dy})`}>
                    <text
                      textAnchor="middle"
                      y={0}
                      style={{
                        fontFamily: "'Rajdhani', sans-serif",
                        fontWeight: 700,
                        fontSize: 12,
                        fill: "#ffffff",
                        paintOrder: "stroke",
                        stroke: "#02152E",
                        strokeWidth: 3.5,
                      }}
                    >
                      {c.city}
                    </text>
                    <text
                      textAnchor="middle"
                      y={14}
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 900,
                        fontSize: 13,
                        fill: "#F7B500",
                        paintOrder: "stroke",
                        stroke: "#02152E",
                        strokeWidth: 4,
                      }}
                    >
                      {c.workers} {c.workers === 1 ? "worker" : "workers"}
                    </text>
                    {c.top_trades?.[0]?.trade && (
                      <text
                        textAnchor="middle"
                        y={27}
                        style={{
                          fontFamily: "'Rajdhani', sans-serif",
                          fontWeight: 600,
                          fontSize: 10,
                          fill: "#06B6D4",
                          paintOrder: "stroke",
                          stroke: "#02152E",
                          strokeWidth: 3,
                        }}
                      >
                        {c.top_trades[0].trade}
                      </text>
                    )}
                  </g>
                </Marker>
              );
            })}
          </ComposableMap>

          {/* Floating tooltip */}
          {hover && (
            <div
              className="pointer-events-none fixed z-50 max-w-[220px] rounded border border-slate-700 bg-slate-900 px-2 py-1.5 font-mono text-xs text-white shadow-lg"
              style={{ top: hover.y + 12, left: hover.x + 12 }}
            >
              <div className="flex items-center gap-1 font-heading text-sm font-bold text-ncc-gold">
                <MapPin className="h-3.5 w-3.5" /> {hover.city}
              </div>
              <div className="mt-0.5">
                <b>{hover.workers}</b> workers · {hover.records} contractor{hover.records === 1 ? "" : "s"}
              </div>
              {hover.top_trades?.length > 0 && (
                <div className="mt-1 border-t border-slate-700 pt-1 text-[10px]">
                  <div className="industrial-label text-slate-400">Top Trades</div>
                  {hover.top_trades.slice(0, 3).map((t) => (
                    <div key={t.trade} className="flex justify-between">
                      <span className="truncate">{t.trade}</span>
                      <span className="ml-2 text-cyan-300">{t.workers}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-1 text-[10px] text-cyan-300">▸ Click to view directory</div>
            </div>
          )}

          {/* Map caption / legend */}
          <div className="pointer-events-none absolute bottom-2 left-2 rounded bg-black/55 px-2 py-1 font-mono text-[10px] text-white backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-[#06B6D4] ring-1 ring-white" />
              <span>City (size = workers)</span>
            </div>
            <div className="mt-0.5 text-slate-300">{plotted.length} of {cities.length} cities mapped</div>
          </div>
        </div>

        {/* City list w/ inline trades — sits BELOW the map (full width) */}
        <div className="flex flex-col">
          <div className="mb-2 industrial-label text-xs text-slate-500">All Cities in {stateName}</div>
          <div className="max-h-[300px] space-y-2 overflow-y-auto pr-1">
            {cities.length === 0 && (
              <div className="py-6 text-center font-mono text-sm text-slate-500">No city data.</div>
            )}
            {cities.map((c) => {
              const hasCoords = !!coordsForCity(c.city);
              return (
                <button
                  key={c.city}
                  onClick={() => {
                    const params = new URLSearchParams();
                    params.set("state", stateName);
                    params.set("city", c.city);
                    if (segment) params.set("segment", segment);
                    navigate(`/directory?${params.toString()}`);
                  }}
                  data-testid={`top-state-city-row-${c.city}`}
                  className="block w-full rounded border border-slate-200 bg-white px-3 py-2 text-left transition-colors hover:border-slate-900 hover:bg-slate-50"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block h-2.5 w-2.5 rounded-full ${hasCoords ? "bg-cyan-500" : "bg-slate-300"}`}
                        title={hasCoords ? "On map" : "Coords unknown"}
                      />
                      <span className="font-heading text-base font-bold text-slate-900 md:text-lg">{c.city}</span>
                    </div>
                    <span className="font-mono text-sm md:text-base">
                      <b className="text-slate-900">{c.workers}</b>
                      <span className="text-slate-500"> / {c.records}</span>
                    </span>
                  </div>
                  {c.top_trades?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {c.top_trades.slice(0, 3).map((t) => (
                        <span
                          key={t.trade}
                          className="rounded border border-slate-300 bg-slate-50 px-2 py-1 font-mono text-xs text-slate-700"
                        >
                          {t.trade} · <b className="text-slate-900">{t.workers}</b>
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
 * Helper: convert a single TopoJSON geometry to a GeoJSON feature
 * (just enough for d3-geo's geoCentroid/geoBounds — handles Polygon
 * and MultiPolygon with quantised arcs + transform.)
 * ------------------------------------------------------------------ */
function topoToGeoFeature(geom, arcs, transform) {
  const { scale = [1, 1], translate = [0, 0] } = transform || {};
  const decodeArc = (arcIdx) => {
    const reversed = arcIdx < 0;
    const idx = reversed ? ~arcIdx : arcIdx;
    const arc = arcs[idx];
    let x = 0, y = 0;
    const points = arc.map(([dx, dy]) => {
      x += dx;
      y += dy;
      return [x * scale[0] + translate[0], y * scale[1] + translate[1]];
    });
    return reversed ? points.slice().reverse() : points;
  };
  const ringToCoords = (ring) => {
    const coords = [];
    ring.forEach((arcIdx, i) => {
      const pts = decodeArc(arcIdx);
      if (i === 0) coords.push(...pts);
      else coords.push(...pts.slice(1));
    });
    return coords;
  };
  let coordinates;
  if (geom.type === "Polygon") {
    coordinates = geom.arcs.map(ringToCoords);
  } else if (geom.type === "MultiPolygon") {
    coordinates = geom.arcs.map((poly) => poly.map(ringToCoords));
  } else {
    coordinates = [];
  }
  return { type: "Feature", properties: geom.properties || {}, geometry: { type: geom.type, coordinates } };
}
